import React, { useState, useEffect } from 'react';
import { Loader2, UserPlus, Shield, ShieldOff, Trash2, ToggleLeft, ToggleRight, Send, Users, Building2, Image as ImageIcon, Activity, MoreVertical, AlertCircle, RefreshCcw, Building } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useOrganization, useOrganizationList } from '@clerk/react';
import { TeamMember, TeamStatRow, UserProfile } from '../types';
import { trackEvent } from '../services/analytics';
import { api } from '../services/api';

type MembersResponse = { members: TeamMember[]; hasOrg: boolean } | TeamMember[];
type StatsResponse =
  | { stats: TeamStatRow[]; memberCount: number; hasOrg: boolean }
  | TeamStatRow[];

function unwrapMembers(r: MembersResponse): { members: TeamMember[]; hasOrg: boolean } {
  if (Array.isArray(r)) return { members: r, hasOrg: r.length > 0 };
  return { members: r.members, hasOrg: r.hasOrg };
}
function unwrapStats(r: StatsResponse): { stats: TeamStatRow[]; hasOrg: boolean } {
  if (Array.isArray(r)) return { stats: r, hasOrg: r.length > 0 };
  return { stats: r.stats, hasOrg: r.hasOrg };
}

interface Props {
  user: UserProfile;
}

const fmt = (dt: string | null) => dt
  ? new Date(dt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '-';

const timeAgo = (dt: string | null): string => {
  if (!dt) return 'Nooit';
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Zojuist';
  if (mins < 60) return `${mins} min geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} dag${days > 1 ? 'en' : ''} geleden`;
  return fmt(dt);
};

export const TeamDashboard: React.FC<Props> = ({ user }) => {
  const { organization } = useOrganization();
  const { createOrganization, setActive, isLoaded: orgListLoaded } = useOrganizationList();
  const navigate = useNavigate();

  // Inline org-create state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<TeamStatRow[]>([]);
  const [hasOrg, setHasOrg] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'medewerker' | 'beheerder'>('medewerker');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<number | null>(null);

  useEffect(() => {
    const handler = () => setOpenMenu(null);
    if (openMenu) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenu]);

  const loadData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [membersRes, statsRes] = await Promise.all([
        api.get<MembersResponse>('/api/team/members'),
        api.get<StatsResponse>('/api/team/stats'),
      ]);
      const m = unwrapMembers(membersRes);
      const s = unwrapStats(statsRes);
      setMembers(m.members);
      setStats(s.stats);
      // STA-147: never downgrade hasOrg from true when Clerk already has an
      // active organization. The backend webhook → kantoor_id sync can lag the
      // Clerk createOrganization call by a few seconds; without this guard,
      // a fresh /api/team/members poll returning hasOrg:false would flash the
      // user back to the "create org" empty state right after a successful
      // setActive.
      const clerkSaysHasOrg = Boolean(organization);
      setHasOrg(m.hasOrg || s.hasOrg || clerkSaysHasOrg);
    } catch (err: unknown) {
      Sentry.captureException(err, { tags: { source: 'team-load' } });
      setLoadError(err instanceof Error ? err.message : 'Kon teamgegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !organization) return;
    setInviting(true);
    setError(null);
    try {
      const clerkRole = inviteRole === 'beheerder' ? 'org:admin' : 'org:member';
      await organization.inviteMember({ emailAddress: inviteEmail.trim(), role: clerkRole });
      setInviteResult(`Uitnodiging verstuurd als ${inviteRole}.`);
      trackEvent('team_member_invited', { role: inviteRole });
      setInviteEmail('');
      setInviteRole('medewerker');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      setInviting(false);
    }
  };

  const handleToggleRole = async (memberId: number, currentRole: string) => {
    const newRole = currentRole === 'beheerder' ? 'medewerker' : 'beheerder';
    try {
      await api.patch(`/api/team/members/${memberId}/role`, { role: newRole });
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Rol wijzigen mislukt');
    }
  };

  const handleToggleActive = async (memberId: number) => {
    try {
      await api.patch(`/api/team/members/${memberId}/deactivate`, {});
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Status wijzigen mislukt');
    }
  };

  const handleDelete = async (memberId: number) => {
    if (!confirm('Weet je zeker dat je dit teamlid wilt verwijderen? Dit verwijdert ook al hun woningen en renders.')) return;
    try {
      await api.del(`/api/team/members/${memberId}`);
      loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newOrgName.trim();
    if (!name || !orgListLoaded || !createOrganization) return;
    setCreating(true);
    setCreateError(null);
    try {
      const newOrg = await createOrganization({ name });
      // STA-147: Clerk creates the org but does NOT mark it as the active session
      // org by default. Without setActive, useOrganization() keeps returning null,
      // so the UI never updates and the user sees the empty state again. The
      // backend /api/team/* endpoints also depend on the active org JWT claim.
      // setActive failures are non-fatal: the org exists in Clerk, the user
      // can pick it from the OrganizationSwitcher; we log and continue so
      // they aren't blocked from navigating.
      if (setActive && newOrg?.id) {
        try {
          await setActive({ organization: newOrg.id });
        } catch (setActiveErr: unknown) {
          Sentry.captureException(setActiveErr, {
            tags: { source: 'org-create-set-active' },
            level: 'warning',
          });
        }
      }
      trackEvent('organization_created', { source: 'team-dashboard' });
      // Optimistically flip hasOrg so the team panel renders immediately. The
      // Clerk webhook → DB sync (kantoor_id) typically takes 1-3s, during which
      // /api/team/members will still return hasOrg:false. Optimistic render +
      // delayed reload avoids a flash back to the empty state.
      setHasOrg(true);
      setShowCreateForm(false);
      setNewOrgName('');
      // Give the Clerk → backend webhook a moment, then refresh team data.
      // Failures here are swallowed: the optimistic UI is already correct, and
      // the next route visit will fetch fresh data.
      window.setTimeout(() => {
        loadData().catch(() => {});
      }, 1500);
      navigate('/team');
    } catch (err: unknown) {
      Sentry.captureException(err, { tags: { source: 'org-create' } });
      setCreateError(err instanceof Error ? err.message : 'Aanmaken mislukt');
    } finally {
      setCreating(false);
    }
  };

  const totalProps = 0; // property_count not returned by /api/team/stats query
  const totalRenders = stats.reduce((s, m) => s + (m.renders_total || 0), 0);
  const activeMembers = members.filter(m => m.is_active).length;

  const statCards = [
    { label: 'Teamleden', value: members.length, icon: <Users size={16} /> },
    { label: 'Actief', value: activeMembers, icon: <Activity size={16} /> },
    { label: 'Woningen', value: totalProps, icon: <Building2 size={16} /> },
    { label: 'Renders', value: totalRenders, icon: <ImageIcon size={16} /> },
  ];

  return (
    <main className="min-h-screen bg-bg">
      <div className="max-w-6xl mx-auto w-full px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Team</p>
            <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">{user.companyName}</h1>
            <p className="text-sm text-ink/55 mt-2">Beheer je collega's, rollen en activiteit op één plek.</p>
          </div>
          <button
            onClick={() => { setShowInvite(!showInvite); setInviteResult(null); }}
            className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] transition-colors self-start md:self-auto"
          >
            <UserPlus size={15} /> Collega uitnodigen
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-ink/40">
            <Loader2 size={20} className="animate-spin mr-2" /> Laden…
          </div>
        ) : loadError ? (
          <div className="bg-white rounded-2xl border border-red-200 p-8 flex flex-col items-center text-center">
            <AlertCircle size={28} className="text-red-500 mb-3" />
            <h2 className="font-sans text-lg font-bold text-ink mb-1">Kon teamgegevens niet laden</h2>
            <p className="text-sm text-ink/55 mb-5 max-w-md">{loadError}</p>
            <button
              type="button"
              onClick={loadData}
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            >
              <RefreshCcw size={14} /> Opnieuw proberen
            </button>
          </div>
        ) : !hasOrg ? (
          <div data-testid="no-org-panel" className="bg-white rounded-2xl border border-black/8 p-10 flex flex-col items-center text-center max-w-xl mx-auto">
            <span className="w-12 h-12 rounded-xl bg-accent/10 text-accent grid place-items-center mb-4">
              <Building size={22} />
            </span>
            <h2 className="font-sans text-xl font-bold text-ink mb-2">Maak een kantoor aan</h2>
            <p className="text-sm text-ink/55 mb-6 max-w-md">
              Je bent nog niet aan een kantoor gekoppeld. Maak hieronder een kantoor aan om collega's uit te nodigen en het teamoverzicht te bekijken.
            </p>

            {!showCreateForm ? (
              <button
                type="button"
                data-testid="show-create-org-btn"
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] transition-colors"
              >
                <Building2 size={14} /> Kantoor aanmaken
              </button>
            ) : (
              <form
                onSubmit={handleCreateOrg}
                data-testid="create-org-form"
                className="w-full max-w-sm text-left"
              >
                <div className="mb-4">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/55 mb-1.5">
                    Naam kantoor *
                  </label>
                  <input
                    type="text"
                    value={newOrgName}
                    onChange={e => setNewOrgName(e.target.value)}
                    placeholder="Naam van uw kantoor"
                    required
                    autoFocus
                    className="w-full border border-black/10 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                  />
                  <p className="text-[11px] text-ink/45 mt-1.5">
                    Voeg uw naam, kvk-nummer en adres toe na het aanmaken via de organisatie-instellingen.
                  </p>
                </div>
                {createError && (
                  <p className="text-sm text-red-600 font-medium mb-3">{createError}</p>
                )}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={creating || !newOrgName.trim()}
                    data-testid="create-org-submit"
                    className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] transition-colors disabled:opacity-50"
                  >
                    {creating ? <Loader2 size={14} className="animate-spin" /> : <Building2 size={14} />}
                    Kantoor aanmaken
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowCreateForm(false); setCreateError(null); setNewOrgName(''); }}
                    className="px-4 py-2.5 rounded-lg text-sm font-semibold text-ink/60 hover:bg-black/5 transition-colors"
                  >
                    Annuleren
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
              {statCards.map(s => (
                <div key={s.label} className="bg-white rounded-2xl border border-black/8 p-5">
                  <div className="flex items-center gap-2 text-ink/45 mb-3">
                    <span className="w-7 h-7 rounded-lg bg-accent/8 text-accent grid place-items-center">{s.icon}</span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">{s.label}</span>
                  </div>
                  <p className="font-sans text-2xl md:text-[28px] font-bold tracking-[-0.02em] text-ink leading-none">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Invite form */}
            {showInvite && (
              <form onSubmit={handleInvite} className="bg-white rounded-2xl border border-black/8 p-6 md:p-7 mb-8">
                <div className="mb-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-1">Uitnodigen</p>
                  <h2 className="font-sans text-xl font-bold tracking-[-0.01em] text-ink">Nieuw teamlid</h2>
                </div>
                <div className="mb-4">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/55 mb-1.5">E-mailadres *</label>
                  <input
                    type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    placeholder="collega@kantoor.nl" required autoFocus
                    className="w-full border border-black/10 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                  />
                </div>
                <div className="mb-5">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/55 mb-2">Rol</label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setInviteRole('medewerker')}
                      className={`text-left p-4 rounded-xl border transition-all ${inviteRole === 'medewerker' ? 'border-accent bg-accent/5 ring-2 ring-accent/20' : 'border-black/10 hover:border-black/20 bg-white'}`}
                    >
                      <div className="text-sm font-semibold text-ink">Medewerker</div>
                      <div className="text-xs text-ink/55 mt-1 leading-relaxed">Kan woningen toevoegen en renderen. Ziet geen team-dashboard.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setInviteRole('beheerder')}
                      className={`text-left p-4 rounded-xl border transition-all ${inviteRole === 'beheerder' ? 'border-accent bg-accent/5 ring-2 ring-accent/20' : 'border-black/10 hover:border-black/20 bg-white'}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-ink">Beheerder</span>
                        <span className="text-[9.5px] bg-accent text-white px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-[0.1em]">Team</span>
                      </div>
                      <div className="text-xs text-ink/55 mt-1 leading-relaxed">Alles van medewerker + team-dashboard, kan collega's uitnodigen en rol wijzigen.</div>
                    </button>
                  </div>
                </div>
                {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
                {inviteResult && <p className="bg-accent/8 text-accent text-sm px-3 py-2 rounded-lg mb-3 font-medium">{inviteResult}</p>}
                <button
                  type="submit" disabled={inviting}
                  className="bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] transition-colors flex items-center gap-2 disabled:opacity-60"
                >
                  {inviting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Verstuur uitnodiging
                </button>
              </form>
            )}

            {/* Members */}
            <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
              <div className="flex items-center justify-between px-6 md:px-7 py-4 border-b border-black/6">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/55">Teamleden</h2>
                <span className="text-xs text-ink/50">{members.length} {members.length === 1 ? 'lid' : 'leden'}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-black/[0.02] text-ink/55">
                      <th className="px-6 py-3 text-left text-[10.5px] font-semibold uppercase tracking-[0.12em]">Naam</th>
                      <th className="px-4 py-3 text-left text-[10.5px] font-semibold uppercase tracking-[0.12em]">Email</th>
                      <th className="px-4 py-3 text-center text-[10.5px] font-semibold uppercase tracking-[0.12em]">Rol</th>
                      <th className="px-4 py-3 text-center text-[10.5px] font-semibold uppercase tracking-[0.12em]">Status</th>
                      <th className="px-4 py-3 text-center text-[10.5px] font-semibold uppercase tracking-[0.12em]">Woningen</th>
                      <th className="px-4 py-3 text-center text-[10.5px] font-semibold uppercase tracking-[0.12em]">Renders</th>
                      <th className="px-4 py-3 text-center text-[10.5px] font-semibold uppercase tracking-[0.12em]">Laatste actief</th>
                      <th className="px-6 py-3 text-right text-[10.5px] font-semibold uppercase tracking-[0.12em]">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map(m => {
                      const isMe = m.id === parseInt(localStorage.getItem('estateflow_user_id') || '0') || m.email === user.email;
                      return (
                        <tr key={m.id} className="border-t border-black/6 hover:bg-black/[0.015] transition-colors">
                          <td className="px-6 py-3.5 font-semibold text-ink">
                            {m.first_name || '-'} {isMe && <span className="text-[10px] text-ink/40 font-medium ml-1">(jij)</span>}
                          </td>
                          <td className="px-4 py-3.5 text-ink/60 text-[13px]">{m.email}</td>
                          <td className="px-4 py-3.5 text-center">
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] rounded-md ${
                              m.role === 'beheerder' || m.role === 'superadmin'
                                ? 'bg-accent/10 text-accent'
                                : 'bg-black/5 text-ink/60'
                            }`}>
                              {m.role}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span className="w-2 h-2 rounded-full inline-block bg-green-500" />
                          </td>
                          <td className="px-4 py-3.5 text-center tabular-nums text-ink/75">—</td>
                          <td className="px-4 py-3.5 text-center tabular-nums text-ink/75">{m.renders_total || 0}</td>
                          <td className="px-4 py-3.5 text-center text-[12px] text-ink/55">{timeAgo(m.last_render_at || m.last_login)}</td>
                          <td className="px-6 py-3.5 relative">
                            {!isMe && (
                              <>
                                <div className="flex items-center justify-end">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === m.id ? null : m.id); }}
                                    className="p-1.5 rounded-md hover:bg-black/5 transition-colors text-ink/60"
                                  >
                                    <MoreVertical size={14} />
                                  </button>
                                </div>
                                {openMenu === m.id && (
                                  <div className="absolute right-6 top-11 z-20 bg-white border border-black/10 rounded-xl shadow-[0_12px_30px_-8px_rgba(0,0,0,0.18)] py-1.5 min-w-[180px]">
                                    <button onClick={() => { handleToggleRole(m.id, m.role); setOpenMenu(null); }} className="w-full text-left px-3.5 py-2 text-sm hover:bg-black/4 transition-colors flex items-center gap-2.5 text-ink/75">
                                      {m.role === 'beheerder' ? <ShieldOff size={14} /> : <Shield size={14} />}
                                      Rol wijzigen
                                    </button>
                                    <button onClick={() => { handleToggleActive(m.id); setOpenMenu(null); }} className="w-full text-left px-3.5 py-2 text-sm hover:bg-black/4 transition-colors flex items-center gap-2.5 text-ink/75">
                                      <ToggleLeft size={14} />
                                      Activatie wijzigen
                                    </button>
                                    <div className="border-t border-black/6 my-1" />
                                    <button onClick={() => { handleDelete(m.id); setOpenMenu(null); }} className="w-full text-left px-3.5 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2.5">
                                      <Trash2 size={14} /> Verwijderen
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};
